import updateDataLayer from '@utils/helpers/gtm';
import { PropsWithChildren } from 'react';
import { A11y, Navigation } from 'swiper/modules';
import { Swiper, SwiperProps } from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/a11y';
import 'swiper/scss/autoplay';
import 'swiper/scss/effect-fade';
import 'swiper/scss/keyboard';
import { SwiperModule } from 'swiper/types';

interface Props extends SwiperProps {
  readonly additionalModules?: SwiperModule[];
  readonly className?: string;
  readonly id?: string;
  readonly navId?: string;
  readonly analyticsEnabled?: boolean;
  readonly trackingContentName?: string;
  readonly onSetIsSwiperBeginning?: (isSwiperBeginning: boolean) => void;
  readonly onSetIsSwiperEnd?: (isSwiperEnd: boolean) => void;
}

const Carousel = ({
  additionalModules = [],
  children,
  className,
  id = 'carousel',
  navId = '',
  analyticsEnabled = false,
  trackingContentName,
  onSetIsSwiperBeginning,
  onSetIsSwiperEnd,
  onResize,
  ...props
}: PropsWithChildren<Props>) => {
  if (!children) {
    return null;
  }

  return (
    <Swiper
      id={id}
      onSlideChangeTransitionEnd={(swiper) => {
        if (analyticsEnabled && swiper.realIndex !== 0) {
          updateDataLayer({
            event: 'carousel',
            interaction: 'tab_change',
            link_text:
              swiper.previousIndex < swiper.activeIndex ? 'Next' : 'Previous',
            ...(trackingContentName
              ? { content_name: trackingContentName }
              : {}),
          });
        }
      }}
      a11y={{ enabled: true }}
      className={className}
      navigation={{
        enabled: !!navId,
        prevEl: `#prevBtn-${navId}`,
        nextEl: `#nextBtn-${navId}`,
      }}
      modules={[...[A11y, Navigation], ...additionalModules]}
      onResize={(swiper) => {
        onResize?.(swiper);
        onSetIsSwiperBeginning?.(swiper.isBeginning);
        onSetIsSwiperEnd?.(swiper.isEnd);
      }}
      onReachBeginning={() => {
        onSetIsSwiperBeginning?.(true);
      }}
      onReachEnd={() => {
        onSetIsSwiperEnd?.(true);
      }}
      onFromEdge={(swiper) => {
        onSetIsSwiperBeginning?.(swiper.isBeginning);
        onSetIsSwiperEnd?.(swiper.isEnd);
      }}
      {...props}
    >
      {children}
    </Swiper>
  );
};

export default Carousel;
